import { useContext } from 'react';

import { Button } from '@selina-finance/ui';
import { useNavigate } from 'react-router-dom';

import { ApplicationContext } from '@src/app/contexts/Application';
import { DebtConContext } from '@src/app/contexts/DebtCon';
import { DebtLabelEnum } from '@src/app/formModels/eligibility/debtCon';
import { CreditCommitmentsData } from '@src/app/interfaces/debtcon';
import { debtTypeConfig, getApplicantName } from '@src/shared/helpers/debtCon';
import { roundNumber, toCurrency } from '@src/shared/helpers/numbers';
import BackButton from '@src/ui/components/BackButton/BackButton';
import DebtConCallOut from '@src/ui/components/CallOut/DebtConCallOut';
import CoinsIcon from '@src/ui/components/Icons/CoinsIcon';
import ProgressMenu from '@src/ui/components/Progress/ProgressMenu';

const YourBorrowingView = () => {
    const navigate = useNavigate();
    const { creditCommitmentsData } = useContext(DebtConContext);
    const { applicants } = useContext(ApplicationContext);

    const totalBorrowing = creditCommitmentsData?.reduce(
        (total: number, item: CreditCommitmentsData) => {
            return total + roundNumber(item?.outstandingBalance ?? 0);
        },
        0,
    );

    const getDebtConfig = (label: DebtLabelEnum) =>
        debtTypeConfig[label] || {
            icon: <CoinsIcon />,
        };

    return (
        <div className='flex flex-row justify-between'>
            <div className='flex flex-col mx-auto sm:ml-0 max-w-400 w-full mt-20 sm:mt-0 sm:mr-10'>
                <div className='hidden lg:block mb-8'>
                    <BackButton />
                </div>
                <div className='text-xl'>
                    <div className='flex flex-col'>
                        <p className='text-2xl leading-100 font-sora-bold lg:text-28 tracking-tighter'>
                            Your current borrowing
                        </p>
                        <p className='leading-6 mt-2 text-lg text-midnight-200'>
                            We’ve found your credit file. Note that this is
                            usually 4-6 weeks out of date.
                        </p>
                        <DebtConCallOut
                            classes='mt-6'
                            text='You can update these details in the next step'
                            includeBaseText={false}
                        />

                        <div className='space-y-2 mt-6'>
                            {creditCommitmentsData?.map(
                                (
                                    currentDebt: CreditCommitmentsData,
                                    index: number,
                                ) => {
                                    const { icon } = getDebtConfig(
                                        currentDebt.type as DebtLabelEnum,
                                    );

                                    const applicantName = getApplicantName(
                                        applicants.length,
                                        currentDebt,
                                        applicants,
                                    );

                                    return (
                                        <div
                                            key={index}
                                            className='flex flex-col bg-white p-4 rounded-lg'
                                        >
                                            <div className='flex justify-between'>
                                                <div className='flex text-sm items-center'>
                                                    {icon}
                                                    <span className='ml-1'>
                                                        {currentDebt.type}
                                                    </span>
                                                </div>
                                                {applicantName && (
                                                    <span className='bg-almond-300 text-xs rounded-full px-2 inline-flex items-center'>
                                                        {applicantName}
                                                    </span>
                                                )}
                                            </div>
                                            <div className='text-right flex justify-between mt-1 items-center'>
                                                <p>
                                                    <span className='font-bold text-sm'>
                                                        £
                                                        {toCurrency(
                                                            currentDebt.monthlyPayment ===
                                                                0 &&
                                                                DebtLabelEnum.CreditCard
                                                                ? roundNumber(
                                                                      (currentDebt.outstandingBalance ??
                                                                          0) *
                                                                          0.025,
                                                                  )
                                                                : roundNumber(
                                                                      currentDebt.monthlyPayment,
                                                                  ),
                                                        )}
                                                    </span>
                                                    <span className='text-xs'>
                                                        /month
                                                    </span>
                                                </p>
                                                <p className='text-sm'>
                                                    <span className='font-bold mr-1'>
                                                        £
                                                        {toCurrency(
                                                            roundNumber(
                                                                currentDebt.outstandingBalance ??
                                                                    0,
                                                            ),
                                                        )}
                                                    </span>
                                                    balance
                                                </p>
                                            </div>
                                        </div>
                                    );
                                },
                            )}
                        </div>

                        <div className='flex justify-between items-center mt-6 p-3 bg-white rounded-lg'>
                            <span className='text-sm'>Total borrowing</span>
                            <span className='font-bold text-lg'>
                                £{toCurrency(totalBorrowing)}
                            </span>
                        </div>

                        <footer className='mt-6'>
                            <Button
                                size='large'
                                color='secondary'
                                className='font-bold w-full text-lg'
                                onClick={() =>
                                    navigate('/eligibility/debt-con/transition')
                                }
                            >
                                <span className='text-lg'>Add details</span>
                            </Button>
                        </footer>
                    </div>
                </div>
            </div>
            <ProgressMenu />
        </div>
    );
};

export default YourBorrowingView;
